import { useEffect, useRef, useContext } from "react" 
import { Play } from "lucide-react"

import { TelemetryContext } from '../context/TelemetryContext'
import { useRegion } from '../hooks/useRegion' 

import { TERMINAL_EVENTS, REGIONS } from '../constants/telemetryEvents'

import '../css/workspace.scss'

// hasTests: false when the instructor turned test cases off, so Run only runs the code
// (VizmentalIDE says "Run" then too).
export default function Terminal({ height, onRun, isRunning, output = [], hasTests = true }) {
  const { logTelemetryEvent } = useContext(TelemetryContext)
  const runLabel = hasTests ? 'Run Tests' : 'Run'
  
  const scrollRef = useRef(null)
  const containerRef = useRef(null)
  
  useRegion(REGIONS.TERMINAL, containerRef, 10)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [output])

  // Track selection and copy events
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    let lastSelection = ''

    const handleSelectionChange = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText && selectedText !== lastSelection) {
        lastSelection = selectedText
        logTelemetryEvent(TERMINAL_EVENTS.SELECT, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      } else if (!selectedText) {
        lastSelection = ''
      }
    }

    const handleCopy = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText) {
        logTelemetryEvent(TERMINAL_EVENTS.COPY, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      }
    }

    el.addEventListener('mouseup', handleSelectionChange)
    el.addEventListener('keyup', handleSelectionChange)
    el.addEventListener('copy', handleCopy)

    return () => {
      el.removeEventListener('mouseup', handleSelectionChange)
      el.removeEventListener('keyup', handleSelectionChange)
      el.removeEventListener('copy', handleCopy)
    }
  }, [logTelemetryEvent])

  return (
    <div ref={containerRef} className="ws-terminal-panel" style={{ height: `${height}px` }}>
      <div className="ws-terminal-controls">
        <div className="ws-controls-left">
          <button 
            className="ws-run-button" 
            onClick={onRun}
            disabled={isRunning} 
          >
            <Play className="ws-icon-sm" /> 
            {isRunning ? 'Running...' : runLabel}
          </button>
        </div>
      </div>

      <div className="ws-terminal-output" ref={scrollRef}>
        <div className="ws-output-content">
          {output.length === 0 && `Click '${runLabel}' to execute your code...`}
          
          {output.map((line, index) => (
             <div key={index} className={`terminal-line ${line.type}`}>
               {line.content} 
             </div>
          ))}
        </div>
      </div>
    </div>
  )
}